import { DndContext, DragOverlay } from '@dnd-kit/core';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Range as LsTypesRange } from 'vscode-languageserver-types';
import BottomActionButtons from '../Components/AppBars/BottomBar/BottomActionsButtons';
import BottomControlBar from '../Components/AppBars/BottomBar/BottomControlBar';
import DeployButton from '../Components/AppBars/BottomBar/DeployButton';
import BottomDrawer from '../Components/Drawers/BottomDrawer/BottomDrawer';
import { MonacoEditor } from '../Components/Editor/Monaco/MonacoEditor';
import { projectSlice } from '../redux/ProjectSlice';
import { useAppDispatch, useAppSelector } from '../redux/ReduxHooks';
import dragImage from '../Components/CodeEditor/assets/drag-image.svg';
import CodeEditorLayout from '../Components/CodeEditor/CodeEditorLayout';
import ActionArgumentsDialog from '../Components/CodeEditor/components/ActionArgumentsDialog';
import DeviceSnippetPortDialog from '../Components/CodeEditor/components/DeviceSnippetPortDialog';
import NoPathsDialog from '../Components/CodeEditor/components/NoPathsDialog';
import PathSelectionDialog from '../Components/CodeEditor/components/PathSelectionDialog';
import { useEditorDialogs } from '../hooks/editor/useEditorDialogs';
import { useEditorDragDrop } from '../hooks/editor/useEditorDragDrop';
import { useMonacoEditor } from '../hooks/editor/useMonacoEditor';
import { useLeftDrawerLayout } from '../hooks/ui/useLeftDrawerLayout';
import { useLogging } from '../logging/logging';
import { equals } from 'ramda';
import {
  setActionList,
  setAxisActionList,
  setCustomActionList,
  setFrameworkActionLists,
  setInputBindingList,
  setStartCallConfig,
} from '../redux/EditorSlice';
import { useClearEditorStateWhenCodeEmpty } from '../Components/CodeEditor/ConfigurationTabs/useClearEditorStateWhenCodeEmpty';
import type { ICardComponent } from '../Components/CodeEditor/Devices/DeviceCardBuilder';
import { parseHardwareFromProjectCode } from '../Components/CodeEditor/ConfigurationTabs/hardwareParser';
import { applicationSlice } from '../redux/ApplicationSlice';
import { buildDriveUnitPortOccupanciesFromHardwareCards } from '../utils/buildDriveUnitPortOccupanciesFromHardwareCards';
import { formatPythonProgramWithRuff } from '../utils/pythonFormattingUtils';

const EMPTY_INPUT_BINDINGS: [] = [];

const CodeEditorPage = () => {
  const { t } = useTranslation();
  const { leftDrawer, leftDrawerContentExpanded, leftDrawerWidth, leftDrawerContentWidth, toggleLeftDrawer } = useLeftDrawerLayout('codeEditor');
  const dispatch = useAppDispatch();
  const logger = useLogging();

  const readOnly = useAppSelector((state) => state.project.loadedProject?.readOnly) ?? false;
  const projectCode = useAppSelector((state) => state.project.loadedProject?.program?.code ?? '');
  const existingInputBindings = useAppSelector((state) => state.editor.inputBindingList) ?? EMPTY_INPUT_BINDINGS;

  const [hardwareDynamicCards, setHardwareDynamicCards] = useState<ICardComponent[]>([]);
  const [hardwareIsLoading, setHardwareIsLoading] = useState(false);
  const [hardwareHasParsed, setHardwareHasParsed] = useState(false);
  const [pythonIsFormatting, setPythonIsFormatting] = useState(false);
  const [driveUnitPortRegistryRev, setDriveUnitPortRegistryRev] = useState(0);
  const existingInputBindingsRef = useRef(existingInputBindings);
  existingInputBindingsRef.current = existingInputBindings;
  const lspParseRunIdRef = useRef(0);
  const hardwareCardsRef = useRef<ICardComponent[]>([]);

  const handleLeftDrawer = useCallback(() => {
    // Prefer centralized hook toggle for consistency
    if (toggleLeftDrawer) {
      toggleLeftDrawer();
    } else {
      dispatch(projectSlice.actions.toggleEditorOpenDrawer());
    }
  }, [dispatch, toggleLeftDrawer]);

  const {
    editorState,
    editorRef,
    standaloneEditorRef,
    editorInstance,
    previewModelRef,
    handleEditorMounted,
    onUpdateCode,
    debouncedSync,
    lspClient,
  } = useMonacoEditor();

  useEffect(() => {
    hardwareCardsRef.current = hardwareDynamicCards;
  }, [hardwareDynamicCards]);

  useClearEditorStateWhenCodeEmpty(projectCode, dispatch, () => {
    setHardwareDynamicCards([]);
    setHardwareIsLoading(false);
    setHardwareHasParsed(false);
    hardwareCardsRef.current = [];
    setDriveUnitPortRegistryRev(0);
    dispatch(applicationSlice.actions.resetDriveUnitPorts());
  });

  useEffect(() => {
    if (projectCode === '') return;
    // Prevent empty-state flash: treat hardware as loading until we complete a parse for this code.
    setHardwareHasParsed(false);
    setHardwareIsLoading(true);
  }, [projectCode]);

  useEffect(() => {
    if (projectCode === '') return;

    // Parse immediately using text scanning; later runs refine results once LSP/editor are ready.
    lspParseRunIdRef.current += 1;
    const runId = lspParseRunIdRef.current;
    setHardwareIsLoading(true);
    const handle = setTimeout(async () => {
      try {
        const existingForParse = existingInputBindingsRef.current ?? EMPTY_INPUT_BINDINGS;
        const parsed = await parseHardwareFromProjectCode({
          code: projectCode,
          editor: editorInstance ?? undefined,
          lspClient,
          existingInputBindings: existingForParse,
          logger,
        });
        if (runId !== lspParseRunIdRef.current) return;

        dispatch(setActionList(parsed.actionList));
        dispatch(setAxisActionList(parsed.axisActionList));
        dispatch(setCustomActionList(parsed.customActionList));
        dispatch(setFrameworkActionLists(parsed.frameworkActionLists));
        dispatch(setStartCallConfig(parsed.startCallConfig));
        if (!equals(parsed.inputBindingList, existingForParse)) {
          dispatch(setInputBindingList(parsed.inputBindingList));
        }
        const prevCards = hardwareCardsRef.current;
        const nextCards = parsed.dynamicCards;
        if (!equals(prevCards, nextCards)) {
          setDriveUnitPortRegistryRev((n) => n + 1);
        }
        dispatch(applicationSlice.actions.clearNonSyntheticDriveUnitPorts());
        const driveUnitRows = buildDriveUnitPortOccupanciesFromHardwareCards(nextCards, editorInstance ?? undefined, projectCode);
        for (const row of driveUnitRows) {
          dispatch(applicationSlice.actions.onUpdateDriveUnitPorts(row));
        }
        setHardwareDynamicCards((prev) => (equals(prev, nextCards) ? prev : nextCards));
      } catch (err) {
        // If LSP times out/throws, we keep the text-only results.
        logger.error('Error parsing mechanisms', err);
      } finally {
        if (runId === lspParseRunIdRef.current) {
          setHardwareIsLoading(false);
          setHardwareHasParsed(true);
        }
      }
    }, 50);

    return () => {
      clearTimeout(handle);
      setHardwareIsLoading(false);
    };
  }, [projectCode, editorInstance, lspClient, dispatch, logger]);

  const handleSelectRange = useCallback(
    (range: LsTypesRange) => {
      if (!editorRef.current) return;
      editorRef.current.selectRange(range as unknown as LsTypesRange);
    },
    [editorRef]
  );

  const dialogs = useEditorDialogs({
    standaloneEditorRef,
    debouncedSync,
  });

  const dragDrop = useEditorDragDrop({
    standaloneEditorRef,
    previewModelRef,
    debouncedSync,
    onPathDropRequested: dialogs.requestPathDrop,
    onNoPathsAvailable: dialogs.requestNoPathsDialog,
    onActionArgsDropRequested: dialogs.requestActionArgsDrop,
    onDeviceSnippetPortDropRequested: dialogs.requestDeviceSnippetPortDrop,
    onDuplicateDriveBaseDropRequested: dialogs.requestDuplicateDriveBaseDialog,
  });

  const handleFormatPythonProgram = useCallback(async () => {
    const editor = standaloneEditorRef.current;
    const model = editor?.getModel();
    if (!editor || !model || readOnly || pythonIsFormatting) return;

    setPythonIsFormatting(true);
    try {
      const currentCode = model.getValue();
      const formattedCode = await formatPythonProgramWithRuff(currentCode);
      if (formattedCode === currentCode) return;

      editor.pushUndoStop();
      editor.executeEdits('python.formatProgram', [{ range: model.getFullModelRange(), text: formattedCode, forceMoveMarkers: true }]);
      editor.pushUndoStop();
      onUpdateCode(formattedCode);
    } finally {
      setPythonIsFormatting(false);
    }
  }, [onUpdateCode, pythonIsFormatting, readOnly, standaloneEditorRef]);

  const formatPythonActionLabel = t('bottomControlBar.formatPython', 'Format Python code');

  return (
    <DndContext
      onDragCancel={dragDrop.handleDragCancel}
      onDragEnd={dragDrop.handleDrop}
      onDragStart={dragDrop.handleDragStart}
      onDragMove={dragDrop.handleDragging}
      collisionDetection={dragDrop.fixCursorSnapOffset}
    >
      <Box height="100vh" width="100vw" minWidth={0} minHeight={0} sx={{ display: 'flex' }} overflow="hidden">
        <CodeEditorLayout
          readOnly={readOnly}
          leftDrawer={leftDrawer ?? false}
          drawerWidth={leftDrawerContentWidth}
          editorInstance={editorInstance}
          editorState={editorState}
          onLeftDrawerToggle={handleLeftDrawer}
          hardwareDynamicCards={hardwareDynamicCards}
          hardwareIsLoading={hardwareIsLoading || !hardwareHasParsed}
          driveUnitPortRegistryRev={driveUnitPortRegistryRev}
        >
          <Stack direction="row" py={0} pr={0} pl={0} flexGrow={1} minWidth={0} minHeight={0} alignItems={'flex-start'}>
            <MonacoEditor
              ref={editorRef}
              onEditorMounted={handleEditorMounted}
              dropInfo={dragDrop.dropInfo}
              lspClient={lspClient}
              code={editorState.code}
              previewModelRef={previewModelRef}
              readOnly={readOnly}
              diagnostics={editorState.diagnostics}
              onUpdateCode={onUpdateCode}
              formatPythonActionLabel={formatPythonActionLabel}
              onFormatPythonProgram={handleFormatPythonProgram}
            />
          </Stack>
          <BottomDrawer
            diagnostics={editorState.diagnostics}
            onSelectRange={handleSelectRange}
            displayActivityIndicator={editorState.isWaitingForResponse}
            leftDrawerOpen={leftDrawerContentExpanded}
            leftDrawerWidth={leftDrawerWidth}
          />
        </CodeEditorLayout>
        {/* Bottom Control Bar */}
        <BottomControlBar
          onUndo={() => editorInstance?.trigger('keyboard', 'undo', null)}
          onRedo={() => editorInstance?.trigger('keyboard', 'redo', null)}
          onZoomIn={() => editorInstance?.trigger('keyboard', 'editor.action.fontZoomIn', null)}
          onZoomOut={() => editorInstance?.trigger('keyboard', 'editor.action.fontZoomOut', null)}
          onZoomReset={() => editorInstance?.trigger('keyboard', 'editor.action.fontZoomReset', null)}
          canUndo={true}
          canRedo={true}
          centerContent={<BottomActionButtons />}
          rightContent={<DeployButton />}
          leftDrawerOpen={leftDrawerContentExpanded}
          leftDrawerWidth={leftDrawerWidth}
        />
      </Box>
      <DragOverlay zIndex={1500} modifiers={[dragDrop.snapLeftToCursor]}>
        <Box width={100} sx={{ opacity: 0.5 }}>
          <img src={dragImage} alt="drag icon" style={{ width: '100px', height: 'auto', pointerEvents: 'none' }} />
        </Box>
      </DragOverlay>

      <PathSelectionDialog
        open={dialogs.pathDialogOpen}
        onClose={dialogs.handlePathDialogClose}
        selectedPathName={dialogs.selectedPathName}
        setSelectedPathName={dialogs.setSelectedPathName}
        paths={dialogs.paths}
        pendingPathDrop={dialogs.pendingPathDrop}
        onConfirm={dialogs.handlePathDialogConfirm}
      />

      <NoPathsDialog open={dialogs.noPathsDialogOpen} onClose={dialogs.handleNoPathsDialogClose} />

      <Dialog open={dialogs.duplicateDriveBaseDialogOpen} onClose={dialogs.handleDuplicateDriveBaseDialogClose} maxWidth="xs" fullWidth>
        <DialogTitle>{t('editor.duplicateDriveBaseDialog.title', 'Drive Base already configured')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">{t('editor.duplicateDriveBaseDialog.message', dialogs.duplicateDriveBaseMessage)}</Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={dialogs.handleDuplicateDriveBaseDialogClose} autoFocus>
            {t('editor.duplicateDriveBaseDialog.ok', 'OK')}
          </Button>
        </DialogActions>
      </Dialog>

      <ActionArgumentsDialog
        open={dialogs.actionArgsDialogOpen}
        onClose={dialogs.handleActionArgsDialogClose}
        pendingDrop={dialogs.pendingActionDrop}
        pathOptions={dialogs.pathOptions}
        onConfirm={dialogs.handleActionArgsDialogConfirm}
        editor={editorInstance}
        driveUnitPortRegistryRev={driveUnitPortRegistryRev}
      />

      <DeviceSnippetPortDialog
        open={dialogs.deviceSnippetPortDialogOpen}
        onClose={dialogs.handleDeviceSnippetPortDialogClose}
        pending={dialogs.pendingDeviceSnippetPortDrop}
        editor={editorInstance}
        driveUnitPortRegistryRev={driveUnitPortRegistryRev}
        onConfirm={dialogs.handleDeviceSnippetPortDialogConfirm}
      />
    </DndContext>
  );
};

export default CodeEditorPage;
